import { useState, useCallback, useRef, useEffect } from "react";
import { sendChatMessage } from "../api/chatApi";

const CHAT_STORAGE_KEY = "ai-chat-messages";
const CHAT_ID_STORAGE_KEY = "ai-chat-conversation-id";

/**
 * Custom hook for managing AI chat state and interactions
 * Messages persist in sessionStorage across tab navigation and clear on logout
 * @returns {Object} Chat state and handlers
 */
export const useChat = () => {
    // Initialize messages from sessionStorage on mount
    const [messages, setMessages] = useState(() => {
        try {
            const stored = sessionStorage.getItem(CHAT_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Track conversation ID to maintain context across messages
    // Initialize from sessionStorage to persist across page reloads
    const [conversationId, setConversationId] = useState(() => {
        try {
            return sessionStorage.getItem(CHAT_ID_STORAGE_KEY) || null;
        } catch {
            return null;
        }
    });

    // Track if we have an active request to prevent duplicates
    const isLoadingRef = useRef(false);

    // Persist messages to sessionStorage whenever they change
    useEffect(() => {
        try {
            sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
        } catch (err) {
            console.warn("Failed to save chat messages to sessionStorage:", err);
        }
    }, [messages]);

    // Persist conversationId to sessionStorage whenever it changes
    useEffect(() => {
        try {
            if (conversationId) {
                sessionStorage.setItem(CHAT_ID_STORAGE_KEY, conversationId);
            } else {
                sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
            }
        } catch (err) {
            console.warn("Failed to save conversationId to sessionStorage:", err);
        }
    }, [conversationId]);

    const sendMessage = useCallback(async (text) => {
        if (!text?.trim() || isLoadingRef.current) {
            return;
        }

        // Add user message immediately
        const userMessage = {
            id: `msg-${Date.now()}`,
            role: "user",
            content: text.trim(),
            timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setIsLoading(true);
        setError(null);
        isLoadingRef.current = true;

        try {
            // Send message with current conversationId to maintain context
            const response = await sendChatMessage(text, conversationId);

            // Update conversationId from response for next message
            if (response.data.conversationId) {
                setConversationId(response.data.conversationId);
            }

            // Add assistant message with response data
            const assistantMessage = {
                id: `msg-${Date.now() + 1}`,
                role: "assistant",
                content: response.data.answer || "",
                data: response.data.data || null,
                sources: response.data.sources || [],
                metadata: response.data.metadata || null,
                conversationId: response.data.conversationId || null,
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);

        } catch (err) {
            const errorMessage = err.response?.data?.message ||
                err.message ||
                "Something went wrong. Please try again.";
            setError(errorMessage);

            // Add error message to chat
            const errorChatMessage = {
                id: `msg-${Date.now() + 1}`,
                role: "error",
                content: "Sorry, I encountered an error. Please try again.",
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, errorChatMessage]);

        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [conversationId]); // Add conversationId to dependencies

    const clearMessages = useCallback(() => {
        setMessages([]);
        setError(null);
        setConversationId(null); // Reset conversation ID to start fresh
        sessionStorage.removeItem(CHAT_STORAGE_KEY);
        sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
    }, []);

    const retryLastMessage = useCallback(() => {
        if (messages.length === 0) return;

        // Find the last user message
        const lastUserMessage = [...messages]
            .reverse()
            .find((msg) => msg.role === "user");

        if (lastUserMessage) {
            // Remove all messages after and including the last user message
            const lastUserIndex = messages.findIndex(
                (msg) => msg.id === lastUserMessage.id
            );
            setMessages((prev) => prev.slice(0, lastUserIndex));
            setError(null);

            // Resend the message
            sendMessage(lastUserMessage.content);
        }
    }, [messages, sendMessage]);

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        clearMessages,
        retryLastMessage,
        hasMessages: messages.length > 0,
    };
};

export default useChat;
